import { useMemo } from 'react';
import {
  useLocation,
  useNavigate,
} from 'react-router-dom';
import {
  RefreshCw,
  Receipt,
  CreditCard,
  TrendingUp,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  useInvoices,
  usePaymentBatches,
} from '@/features/finance';
import { InvoicesView } from './invoices-view';
import { PaymentsView } from './payments-view';
import { SpendView } from './spend-view';

const TABS = [
  {
    id: 'invoices',
    label: 'Invoices',
    href: '/finance/invoices',
    icon: Receipt,
  },
  {
    id: 'payments',
    label: 'Payments',
    href: '/finance/payments',
    icon: CreditCard,
  },
  {
    id: 'spend',
    label: 'Spend Analytics',
    href: '/finance/spend',
    icon: TrendingUp,
  },
];

export function FinancePage() {
  const location = useLocation();
  const navigate = useNavigate();

  const activeTab = useMemo(() => {
    if (location.pathname.includes('/payments')) return 'payments';
    if (location.pathname.includes('/spend')) return 'spend';
    return 'invoices';
  }, [location.pathname]);

  const {
    refetch: refetchInvoices,
    isFetching: isFetchingInvoices,
  } = useInvoices();
  const {
    refetch: refetchPayments,
    isFetching: isFetchingPayments,
  } = usePaymentBatches();

  const handleRefresh = () => {
    if (activeTab === 'payments') {
      refetchPayments();
    } else {
      refetchInvoices();
    }
  };

  const isRefreshing = activeTab === 'payments' ? isFetchingPayments : isFetchingInvoices;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Finance</h1>
          <p className="text-muted-foreground">Manage invoices and accounts payable</p>
        </div>
        <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isRefreshing}>
          <RefreshCw className={`mr-2 h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      <div className="border-b">
        <nav className="flex gap-4">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => navigate(tab.href)}
                className={`flex items-center gap-2 border-b-2 px-1 py-3 text-sm font-medium transition-colors ${
                  isActive
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:border-muted-foreground/50 hover:text-foreground'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {activeTab === 'payments' && <PaymentsView />}
      {activeTab === 'spend' && <SpendView />}
      {activeTab === 'invoices' && <InvoicesView />}
    </div>
  );
}
